import { noop } from 'lodash';
import { FC, useEffect, useState } from 'react';
import { DesignerSetting } from '@app/DesignerSetting';
import {
  Input,
  InputFieldType,
  NoInputTimeoutOverride,
  Select,
  Switch
} from '@app/form_components';
import { CallEncryption } from '@app/form_components/CallEncryption';
import { ErrorHandling } from '@app/form_components/ErrorHandling';
import { ExceptionHandling } from '@app/form_components/ExceptionHandling';
import { IntentHandling } from '@app/form_components/IntentHandling';
import { LoopHandling } from '@app/form_components/LoopHandling';
import { OldItemList } from '@app/form_components/OldItemList';
import { useDesign, useServices } from '@app/hooks';
import { DesignRootFeatureFlags } from '@app/page-components';
import {
  DesignCsvData,
  DesignFunction,
  DesignGraphQlSchema,
  DesignRuntimeEngine,
  ItemListView,
  Option
} from '@app/types';
import { Utility } from '@app/utilities/Utility';
import { ExportWidget } from '@app/widgets/ExportWidget';

const runtimeEngineOptions: Option<DesignRuntimeEngine>[] = [
  {
    label: 'GraalVM',
    value: DesignRuntimeEngine.graalVm
  },
  {
    label: 'Nashorn',
    value: DesignRuntimeEngine.nashorn
  }
];

export const SettingsBasicPage: FC = () => {
  const { globalModalsService } = useServices();
  const serialized = useDesign();
  const { rootSettings } = serialized;
  const [oasysUrl, setOasysUrl] = useState(rootSettings.ameliaUrl ?? '');
  const [csvData, setCsvData] = useState(serialized.csvData);
  const [description, setDescription] = useState(serialized.description);
  const [functions, setFunctions] = useState(serialized.functions);
  const [graphQLSchemas, setGraphQLSchemas] = useState(serialized.graphQLSchemas);
  const [noInputTimeoutOverride, setNoInputTimeoutOverride] = useState(
    rootSettings.noInputTimeoutOverride
  );
  const [runtimeEngine, setRuntimeEngine] = useState(
    rootSettings.runtimeEngine ?? DesignRuntimeEngine.nashorn
  );
  const [sensitiveOverridesEnabled, setSensitiveOverridesEnabled] = useState(
    serialized.sensitiveOverrides?.enabled ?? false
  );
  const [allowSessionVariableAccess, setAllowSessionVariableAccess] = useState(
    rootSettings.allowSessionVariableAccess ?? false
  );
  const labels = window.l.settings;
  const isCommonModule = Utility.getIsCommonModule(serialized);
  const isClientView = DesignerSetting.s.is_public === 'true';

  useEffect(() => {
    const { rootSettings } = serialized;
    setOasysUrl(rootSettings.ameliaUrl ?? '');
    setCsvData(serialized.csvData);
    setDescription(serialized.description);
    setFunctions(serialized.functions);
    setGraphQLSchemas(serialized.graphQLSchemas);
    setNoInputTimeoutOverride(rootSettings.noInputTimeoutOverride);
    setRuntimeEngine(rootSettings.runtimeEngine ?? DesignRuntimeEngine.nashorn);
    setSensitiveOverridesEnabled(serialized.sensitiveOverrides?.enabled ?? false);
    setAllowSessionVariableAccess(rootSettings.allowSessionVariableAccess ?? false);
  }, [serialized]);

  return (
    <>
      {(isCommonModule) ? null : (
        <>
          <div className="row">
            <div className="col-md-12">
              <label>{labels.application_basic}</label>
            </div>

            {(serialized.archetypeVersion) ? (
              <div className="col-md-6">
                <Input
                  onChange={noop}
                  name="archetypeVersion"
                  label={labels.archetype_version}
                  tooltip="The version of the Archetype application used to provide the application's pre-configured components."
                  value={serialized.archetypeVersion}
                  disabled={true}
                />
              </div>
            ) : null}

            <div className="col-md-6">
              <Select
                label="Runtime Engine"
                name="runtimeEngine"
                options={runtimeEngineOptions}
                onChange={async (newRuntimeEngine) => {
                  if (!newRuntimeEngine || newRuntimeEngine === runtimeEngine) {
                    return;
                  }

                  const {
                    deprecatedNashorn,
                    wishToProceed
                  } = window.l.popup;
                  const proceed = (newRuntimeEngine === DesignRuntimeEngine.nashorn) ? (
                    await globalModalsService.openConfirmationModal({
                      children: (
                        <div className="box content">
                          <span style={{ color: 'var(--theme-error)' }}>{deprecatedNashorn}</span>
                          <span>{wishToProceed}</span>
                        </div>
                      )
                    })
                  ) : true;
                  if (!proceed) {
                    return;
                  }
                  setRuntimeEngine(newRuntimeEngine);
                  window.apm.setBasic('rootSettings.runtimeEngine', newRuntimeEngine);
                }}
                tooltip="JavaScript runtime engine used when PE runs."
                value={runtimeEngine}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <Input
                name="ameliaUrl"
                label="OASYS URL"
                onChange={(newOasysUrl) => {
                  setOasysUrl(newOasysUrl);
                  window.apm.setBasic('rootSettings.ameliaUrl', newOasysUrl);
                }}
                tooltip="OASYS URL used by OASYS nodes."
                value={oasysUrl}
              />
            </div>
          </div>
        </>
      )}

      <div className="row">
        <div className="col-md-6">
          <Input
            label={labels.description}
            name="description"
            onChange={(newDescription) => {
              setDescription(newDescription);
              window.apm.setBasic('description', newDescription);
            }}
            tooltip="Write a short description of the design's purpose."
            type={InputFieldType.textarea}
            value={description}
          />
        </div>
      </div>
      <hr />

      {(isCommonModule) ? (
        <>
          <div className="row">
            <div className="col-md-6 flex-gap">
              <Switch
                label="Allow Disable Sensitive Data"
                name="sensitiveOverridesEnabled"
                onChange={(newSensitiveOverridesEnabled) => {
                  setSensitiveOverridesEnabled(newSensitiveOverridesEnabled);
                  window.apm.setBasic('sensitiveOverrides', {
                    enabled: newSensitiveOverridesEnabled,
                    lastLocalChange: window.apm.timestamp
                  });
                }}
                tooltip="Select this option to allow Application Designers to override sensitive data settings
                  in the common module when they import it into an application.
                  When imported, data marked as sensitive in the common module is no longer marked as sensitive in the application."
                value={sensitiveOverridesEnabled}
              />

              <Switch
                label="Allow Session Variable Access"
                name="allowSessionVariableAccess"
                onChange={(newAllowSessionVariableAccess) => {
                  setAllowSessionVariableAccess(newAllowSessionVariableAccess);
                  window.apm.setBasic('rootSettings.allowSessionVariableAccess', newAllowSessionVariableAccess);
                }}
                tooltip="This common module is used for a specific client and can access and/or modify session variables."
                value={allowSessionVariableAccess}
              />
            </div>
          </div>
          <hr />
        </>
      ) : (
        <>
          {(!isClientView) ? null : (
            // Display here because client view does not get the save page.
            <ExportWidget />
          )}

          <div className="row">
            <div className="col-md-12">
              <label>{labels.globalErrorHandling}</label>
            </div>
            <div className="col-md-12">
              <ErrorHandling />
            </div>
          </div>
          <hr />

          <div className="row">
            <div className="col-md-12">
              <label>{labels.exception_handling}</label>
            </div>
            <div className="col-md-12">
              <ExceptionHandling />
            </div>
          </div>
          <hr />

          <div className="row">
            <div className="col-md-12">
              <label>{labels.loopHandling}</label>
            </div>
            <div className="col-md-12">
              <LoopHandling />
            </div>
          </div>
          <hr />

          <div className="row">
            <div className="col-md-12">
              <label>{labels.intentHandling}</label>
            </div>
            <div className="col-md-12">
              <IntentHandling />
            </div>
          </div>
          <hr />

          <DesignRootFeatureFlags />
          <hr />

          <div className="row">
            <div className="col-md-12">
              <label>{labels.fullCallEncryption}</label>
              <CallEncryption />
            </div>
          </div>
          <hr />

          <div className="row">
            <div className="col-md-12">
              <label data-tip={window.l.property?.noInputTimeout_tooltip}>
                {window.l.property.noInputTimeout_global.toUpperCase()}
                <i className="fe icon-info"/>
              </label>
              <NoInputTimeoutOverride
                onValueChange={(timeoutOverrides) => {
                  setNoInputTimeoutOverride(timeoutOverrides);
                  window.apm.setBasic('rootSettings.noInputTimeoutOverride', timeoutOverrides);
                }}
                value={noInputTimeoutOverride}
              />
            </div>
          </div>
          <hr/>

          <div className="row">
            <div className="col-md-12">
              <label
                data-tip="JavaScript code blocks available for reuse by Custom Script objects. Click + to add a new function."
              >
                {labels.reusable_functions.toUpperCase()}
                <i className="fe icon-info"/>
              </label>
            </div>
            <div className="col-md-12">
              <OldItemList<DesignFunction>
                context={{
                  getConfig: () => functions,
                  props: {},
                  setConfig: (_key, newFunctions) => {
                    setFunctions(newFunctions);
                    window.apm.setBasic('functions', newFunctions);
                  }
                }}
                label={labels.functions}
                valKey="functions"
                value={functions}
                view={ItemListView.function}
              />
            </div>
          </div>
          <hr />
        </>
      )}

      <div className="row">
        <div className="col-md-12">
          <label
            data-tip="Data Access Nodes reference GraphQL schemas. This section lists queries and mutations in each schema. Click + to upload a new schema."
          >
            {labels.data_access}
            <i className="fe icon-info"/>
          </label>
        </div>
        <div className="col-md-12">
          <OldItemList<DesignGraphQlSchema>
            context={{
              getConfig: () => graphQLSchemas,
              props: {},
              setConfig: (_key, newGraphQLSchemas) => {
                setGraphQLSchemas(newGraphQLSchemas);
                window.apm.setBasic('graphQLSchemas', newGraphQLSchemas);
              }
            }}
            label={labels.graphql_schemas}
            valKey="graphQLSchemas"
            value={graphQLSchemas}
            view={ItemListView.schema}
          />
        </div>
      </div>
      <hr />

      <div className="row">
        <div className="col-md-12">
          <label
            data-tip="The CSV files currently uploaded for use by Tabular Router Nodes. This section lists each CSV file and shows the first three data rows in each file. Click on each file to edit it. Click + to upload a new file."
          >
            {labels.tabular_data}
            <i className="fe icon-info"/>
          </label>
        </div>
        <div className="col-md-12">
          <OldItemList<DesignCsvData>
            context={{
              getConfig: () => csvData,
              props: {},
              setConfig: (_key, newCsvData) => {
                setCsvData(newCsvData);
                window.apm.setBasic('csvData', newCsvData);
              }
            }}
            label={labels.csv_data}
            valKey="csvData"
            value={csvData}
            view={ItemListView.csv}
          />
        </div>
      </div>
    </>
  );
};
